/**
 * WatchersPanel
 *
 * Lists task watchers (Avatar + name) with an "auto" badge for creator/assignee.
 * Watcher management is done via the WatcherPickerModal dialog.
 *
 * The list is visible to anyone who can view the task, but EDITING it requires
 * `red_dot:manage` (super admins included) — adding or removing a watcher
 * changes who is notified about someone else's work, so it is a supervisory
 * action rather than part of working the task. Mirrors the Red Dot
 * TaskDetailPanel so the two surfaces agree.
 */

import { useState } from 'react';
import styled from 'styled-components';
import { UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@upskill/shared';
import { Avatar } from '../ui/Avatar/Avatar';
import { Button } from '../ui/Button/Button';
import { useGetWatchers } from '../../hooks/useTasks';
import { WatcherPickerModal } from './WatcherPickerModal';
import { useAuthStore } from '../../stores/authStore';
import { useIsSuperAdmin } from '../../hooks/useIsSuperAdmin';

// ============================================================================
// Props
// ============================================================================

export interface WatchersPanelProps {
  taskId: string;
  /** The task's client — watcher candidates are this client's assigned clinicians. */
  clientId?: string | null;
  /** The task's assignee — excluded from the watcher picker (assignees follow by assignment). */
  assigneeUserId?: string | null;
}

// ============================================================================
// Styled components
// ============================================================================

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const PanelTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const WatcherList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const WatcherItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const WatcherName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  flex: 1;
`;

const AutoBadge = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.primary.base};
  background: ${({ theme }) => theme.colors.status.info.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.info.border};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  padding: 1px ${({ theme }) => theme.spacing[2]};
  white-space: nowrap;
`;

const StatusText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.light};
  margin: 0;
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0;
`;

// ============================================================================
// Component
// ============================================================================

export function WatchersPanel({ taskId, clientId, assigneeUserId }: WatchersPanelProps) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const isSuperAdmin = useIsSuperAdmin();
  const canManageWatchers = isSuperAdmin || hasPermission(PERMISSIONS.RED_DOT_MANAGE);

  const { data: watchers = [], isLoading: watchersLoading, isError: watchersError } =
    useGetWatchers(taskId);

  return (
    <Panel aria-label={t('tasks.detail.watchers.title')}>
      <PanelTitle>{t('tasks.detail.watchers.title')}</PanelTitle>

      {watchersLoading ? (
        <StatusText>{t('tasks.detail.watchers.loadingWatchers')}</StatusText>
      ) : watchersError ? (
        <ErrorText>{t('tasks.detail.watchers.errorWatchers')}</ErrorText>
      ) : watchers.length === 0 ? (
        <StatusText>{t('tasks.detail.watchers.empty')}</StatusText>
      ) : (
        <WatcherList>
          {watchers.map((watcher) => (
            <WatcherItem key={watcher.id}>
              <Avatar
                firstName={watcher.firstName}
                lastName={watcher.lastName}
                size="sm"
              />
              <WatcherName>
                {watcher.firstName} {watcher.lastName}
              </WatcherName>
              {watcher.auto && (
                <AutoBadge>{t('tasks.detail.watchers.autoBadge')}</AutoBadge>
              )}
            </WatcherItem>
          ))}
        </WatcherList>
      )}

      {/* Disable while watcher list is loading — prevents opening the picker
          with an empty currentWatchers, which would seed selected={} and cause
          Save to compute toRemove = all existing watchers unintentionally. */}
      {canManageWatchers && (
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={watchersLoading}
          leftIcon={<UserPlus size={14} aria-hidden="true" />}
        >
          Edit watchers
        </Button>
      )}

      <WatcherPickerModal
        isOpen={canManageWatchers && pickerOpen}
        onClose={() => setPickerOpen(false)}
        taskId={taskId}
        clientId={clientId}
        currentWatchers={watchers}
        assigneeUserId={assigneeUserId}
      />
    </Panel>
  );
}

export default WatchersPanel;
